import React, { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

import { actions } from '../../state';
import { adminPermissionDefinitions } from './admins.data';
import Form from './admins.form';
import { Paper, PaperHeader, TitleSecondary } from '../../components';
import {
  buildAdminPayload,
  getDefaultAdminValues,
  isPermissionSelected,
  setAllPermissionsSelection,
  togglePermissionSelection,
} from './admins.helpers';

const locationPermission = adminPermissionDefinitions.find(
  ({ key }) => key === 'locations'
);

const AddAdmin = ({
  dispatch,
  history,
  admins: { loading, assignableLocations, assignableLocationsLoading },
}) => {
  const [values, setValues] = useState(getDefaultAdminValues());
  const [permissions, setPermissions] = useState([]);
  const [locationIds, setLocationIds] = useState([]);
  const [allLocationsSelected, setAllLocationsSelected] = useState(false);
  const [
    locationEmailNotificationEnabled,
    setLocationEmailNotificationEnabled,
  ] = useState(false);
  const [
    locationSmsNotificationEnabled,
    setLocationSmsNotificationEnabled,
  ] = useState(false);

  const fetchAssignableLocations = useCallback(() => {
    dispatch(actions.admins.getAssignableLocationsAction());
  }, [dispatch]);

  const clearAdmins = useCallback(() => {
    dispatch(actions.admins.clearAdminsAction());
  }, [dispatch]);

  useEffect(() => {
    fetchAssignableLocations();

    return () => clearAdmins();
  }, [clearAdmins, fetchAssignableLocations]);

  const handleInputChange = ({ target: { name, value } }) => {
    const updatedValue = name === 'business_role' ? Number(value) : value;
    setValues({ ...values, [name]: updatedValue });
  };

  const clearLocationAccess = () => {
    setLocationIds([]);
    setAllLocationsSelected(false);
    setLocationEmailNotificationEnabled(false);
    setLocationSmsNotificationEnabled(false);
  };

  const handlePermissionChange = (permission, checked) => {
    const nextPermissions = togglePermissionSelection(
      permissions,
      permission,
      checked
    );

    setPermissions(nextPermissions);

    if (permission.supportsLocationScope && !checked) {
      clearLocationAccess();
    }
  };

  const handleSelectAllPermissions = ({ target: { checked } }) => {
    setPermissions(
      setAllPermissionsSelection(adminPermissionDefinitions, checked)
    );

    if (!checked) {
      clearLocationAccess();
      return;
    }

    setLocationEmailNotificationEnabled(true);
    setLocationSmsNotificationEnabled(true);
  };

  const handleLocationIdsChange = nextLocationIds => {
    const shouldSelectAll =
      !nextLocationIds.length ||
      nextLocationIds.length === assignableLocations.length;

    setAllLocationsSelected(shouldSelectAll);
    setLocationIds(shouldSelectAll ? [] : nextLocationIds);
  };

  const hasLocationsPermission = locationPermission
    ? isPermissionSelected(permissions, locationPermission)
    : false;

  const handleSubmit = e => {
    e.preventDefault();
    const body = {
      ...buildAdminPayload({
        values,
        permissions,
        locationIds,
        allLocationsSelected,
        locationEmailNotificationEnabled,
        locationSmsNotificationEnabled,
        hasLocationsPermission,
        includeEmail: true,
      }),
      business_role: values.business_role || undefined,
      password: values.password,
    };

    dispatch(actions.admins.addAdminAction(body, history));
  };

  return (
    <Paper size="lg">
      <PaperHeader>
        <TitleSecondary>Nauja administratoriaus paskyra</TitleSecondary>
      </PaperHeader>

      <Form
        type="add"
        values={values}
        onChange={handleInputChange}
        onSubmit={handleSubmit}
        loading={loading}
        permissionDefinitions={adminPermissionDefinitions}
        selectedPermissions={permissions}
        onPermissionChange={handlePermissionChange}
        onSelectAllPermissions={handleSelectAllPermissions}
        assignableLocations={assignableLocations}
        assignableLocationsLoading={assignableLocationsLoading}
        selectedLocationIds={locationIds}
        allLocationsSelected={allLocationsSelected}
        onLocationIdsChange={handleLocationIdsChange}
        locationEmailNotificationEnabled={locationEmailNotificationEnabled}
        onLocationNotificationChange={({ target: { checked } }) =>
          setLocationEmailNotificationEnabled(checked)
        }
        locationSmsNotificationEnabled={locationSmsNotificationEnabled}
        onLocationSmsNotificationChange={({ target: { checked } }) =>
          setLocationSmsNotificationEnabled(checked)
        }
      />
    </Paper>
  );
};

const mapStateToProps = state => ({
  admins: state.admins,
});

AddAdmin.propTypes = {
  dispatch: PropTypes.func.isRequired,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  admins: PropTypes.shape({
    loading: PropTypes.bool.isRequired,
    assignableLocations: PropTypes.array.isRequired,
    assignableLocationsLoading: PropTypes.bool.isRequired,
  }).isRequired,
};

export default connect(mapStateToProps)(AddAdmin);
